import React, { useEffect, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { prefersReducedMotion, useInView } from './useInView';

// Demo animada de la importación de Excel (changelog id 50): subir archivo →
// confirmar mapeo → vista previa. Recrea los 3 pasos de ImportAnimalsModal con
// datos de ejemplo. Arranca cuando la sección entra en pantalla; con
// prefers-reduced-motion queda fija en el paso del mapeo.

const TITLES = ['Importar animales (CSV o Excel)', 'Confirmar mapeo', 'Vista previa de importación'];
const STEPS = ['Subir archivo', 'Confirmar mapeo', 'Vista previa'];
const MAPEO: Array<[string, string, 'auto' | 'ia']> = [
  ['Nº Carav.', 'Caravana', 'auto'],
  ['Cat', 'Categoría', 'ia'],
  ['Kgs', 'Peso (kg)', 'ia'],
  ['F. pesada', 'Fecha de evento', 'ia'],
];

const ExcelImportDemo: React.FC = () => {
  const [ref, inView] = useInView<HTMLDivElement>();
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!inView || prefersReducedMotion()) return;
    let cur = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setStep(cur);
      timer = setTimeout(() => {
        cur = (cur + 1) % 3;
        tick();
      }, cur === 1 ? 4200 : 3200);
    };
    tick();
    return () => clearTimeout(timer);
  }, [inView]);

  return (
    <div className="lp-demo" ref={ref} aria-label="Ejemplo de importación de un Excel de animales">
      <div
        className="lp-sheet"
        style={{ transform: step === 0 ? 'none' : 'translate(-14px, 8px)', opacity: step === 2 ? 0.4 : 1 }}
        aria-hidden="true"
      >
        <div className="lp-sheet__top">
          <FileSpreadsheet />
          rodeo_campo_2026.xlsx
        </div>
        <table>
          <tbody>
            <tr><td>Nº Carav.</td><td>Cat</td><td>Kgs</td><td>F. pesada</td></tr>
            <tr><td>032 4821</td><td>vaca</td><td>412</td><td>12/9/26</td></tr>
            <tr><td>032 4822</td><td>vaq</td><td>318</td><td>12/9/26</td></tr>
            <tr><td>032 1120</td><td>ternero</td><td>124</td><td>12-sep</td></tr>
            <tr><td>032 4790</td><td>nov.</td><td>401</td><td>12/9/26</td></tr>
          </tbody>
        </table>
      </div>

      <div className="lp-win">
        <div className="lp-win__h">
          <h3>{TITLES[step]}</h3>
          <div className="lp-wsteps">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="line" />}
                <span className={`s ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`}>
                  <i>{i < step ? '✓' : i + 1}</i>
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="lp-wbody">
          <div className={`lp-pane ${step === 0 ? 'on' : ''}`}>
            <div className="lp-drop">
              <div>
                <div className="lp-file">rodeo_campo_2026.xlsx</div>
                <div className="lp-spin" />
                <p>Analizando tu archivo…</p>
                <small>Detectamos las columnas y te las mostramos para confirmar.</small>
              </div>
            </div>
          </div>
          <div className={`lp-pane ${step === 1 ? 'on' : ''}`}>
            {MAPEO.map(([src, dst, kind]) => (
              <div className="lp-map-row" key={src}>
                <span className="src">{src}</span>
                <span className="arr">→</span>
                <span className="dst">
                  {dst}
                  <span className={`lp-tag ${kind === 'auto' ? 'lp-tag--auto' : 'lp-tag--ia'}`}>
                    {kind === 'auto' ? 'Detectada' : 'Sugerida'}
                  </span>
                </span>
              </div>
            ))}
            <p className="lp-map-note">Revisá la propuesta y cambiá lo que quieras antes de seguir.</p>
          </div>
          <div className={`lp-pane ${step === 2 ? 'on' : ''}`}>
            <div className="lp-ok">
              <div>
                <b>1.249 animales</b>
                <br />
                <span>Listos para importar · 1 fecha corregida</span>
              </div>
              <span className="lp-btn lp-btn--primary">Importar</span>
            </div>
            <table className="lp-pv">
              <thead>
                <tr><th>Caravana</th><th>Categoría</th><th>Peso</th><th>Fecha</th></tr>
              </thead>
              <tbody>
                <tr><td>032 4821</td><td><span className="lp-cat">Vaca</span></td><td>412 kg</td><td>12/09/2026</td></tr>
                <tr><td>032 4822</td><td><span className="lp-cat">Vaquillona</span></td><td>318 kg</td><td>12/09/2026</td></tr>
                <tr className="fix"><td>032 1120</td><td><span className="lp-cat">Ternero</span></td><td>124 kg</td><td>12/09/2026 <em>✓ corregida</em></td></tr>
                <tr><td>032 4790</td><td><span className="lp-cat">Novillo</span></td><td>401 kg</td><td>12/09/2026</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <p className="lp-note lp-demo-cap">Datos de ejemplo.</p>
    </div>
  );
};

export default ExcelImportDemo;
